import { SuperInput } from "@components:SuperInput/SuperInput";
import { Box, Typography } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { ObjectNode, OneOfNode } from "../types";
import { useNullableField } from "../useNullableField";
import { FieldLabel } from "./FieldLabel";
import { FieldBody } from "./FieldRenderer";

/*
 * An optional nested object or union of objects (e.g. audio.vad_parameters, timeline.model) with a switch to enable.
 * While disabled, its value stays null and none of its fields are registered.
 */
export const NullableObjectField = memo(
  ({ node }: { node: ObjectNode | OneOfNode }) => {
    const { t } = useTranslation();
    const { enabled, toggle } = useNullableField(node);

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <SuperInput.Switch
            checked={enabled}
            onChange={(e) => toggle(e.target.checked)}
            inputProps={{
              "aria-label": t("schemaForm.enabledLabel", { name: node.title }),
            }}
            sx={{ mt: 0.5 }}
          />

          <FieldLabel title={node.title} description={node.description} />
        </Box>

        {enabled ? (
          <Box
            sx={{
              mt: 0.5,
              p: 2,
              pb: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <FieldBody node={node} />
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic", mt: 0.5 }}
          >
            {t("schemaForm.enableToConfigure")}
          </Typography>
        )}
      </Box>
    );
  },
);

NullableObjectField.displayName = "NullableObjectField";
